import { createContext, use, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { STAGE } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { PortalContainerProvider } from '@/shared/ui/lib/portal';
import { dp } from '../units';
import { clientToStage, fitStage, stageToClient, type ClientPoint, type Point, type Size, type StageFit } from './geometry';

/**
 * `<Stage>` (a.k.a. ScaledViewport, docs/design/02-constructor.md §8.2–8.3): a fixed design-px stage (1920×1080 by
 * default) scaled uniformly into its host with one CSS transform.
 *
 * Content is authored with the normal rem-based Tailwind utilities (`w-308 px-16 text-sm` = design px). The root
 * font-size already scales rem with the viewport (tokens.css), so the stage transform divides that factor back out:
 * `transform = scale(fit × zoom / uiScale)` ⇒ exactly `fit × zoom` CSS px per design px, independent of the root size.
 *
 * - `fit="contain"`: letterbox (Primary BG/1000 bars), centred. `fit="width"`: fill the width, scroll vertically.
 * - Measured with a ResizeObserver inside a layout effect, and hidden until measured → no unscaled first frame.
 * - A portal container lives inside the stage (`useStagePortal()`; also provided to every `shared/ui` overlay via
 *   `PortalContainerProvider`), so popovers/menus/dialogs opened inside the stage scale with it.
 * - `clientToStage()` converts pointer events (any transform/zoom/pan) to stage px; `useStageDrag()` for editors.
 */

export interface StageContextValue {
  /** design size of the stage */
  width: number;
  height: number;
  fit: StageFit;
  /** CSS px per design px (fit × zoom) — the visual scale */
  scale: number;
  /** CSS px per design px of the un-transformed rem layout (root font-size / 16) */
  uiScale: number;
  /** false until the first measurement */
  ready: boolean;
  stageElement: HTMLDivElement | null;
  /** portal target inside the stage (Base UI `Portal container`) */
  portalContainer: HTMLDivElement | null;
  clientToStage: (e: ClientPoint) => Point & { s: number };
  stageToClient: (p: Point) => Point;
}

const StageContext = createContext<StageContextValue | null>(null);

export interface StageLayoutInfo {
  scale: number;
  uiScale: number;
  /** stage top-left inside the host, CSS px */
  offset: Point;
  host: Size;
}

export interface StageProps {
  width?: number;
  height?: number;
  fit?: StageFit;
  /** editor zoom on top of the fit (1 = fit) */
  zoom?: number;
  /** editor pan in CSS px, added to the centred position */
  pan?: Point;
  /** clip content to the stage box (default). Editors may show off-stage parts with `false`. */
  clip?: boolean;
  /** host classes — the host fills its parent; its background is the letterbox colour */
  className?: string;
  stageClassName?: string;
  stageStyle?: CSSProperties;
  /** called whenever the scale/offset changes */
  onLayout?: (info: StageLayoutInfo) => void;
  /** rendered in the host, outside the scaled stage (CSS px overlays such as selection handles) */
  overlay?: ReactNode;
  children?: ReactNode;
  'aria-label'?: string;
}

interface Layout {
  s: number;
  ui: number;
  ox: number;
  oy: number;
  hostW: number;
  hostH: number;
}

const sameLayout = (a: Layout | null, b: Layout) =>
  a !== null && a.s === b.s && a.ui === b.ui && a.ox === b.ox && a.oy === b.oy && a.hostW === b.hostW && a.hostH === b.hostH;

export function Stage({
  width = STAGE.width,
  height = STAGE.height,
  fit = 'contain',
  zoom = 1,
  pan,
  clip = true,
  className,
  stageClassName,
  stageStyle,
  onLayout,
  overlay,
  children,
  ...rest
}: StageProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  const panX = pan?.x ?? 0;
  const panY = pan?.y ?? 0;

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !stageEl) return;
    const measure = () => {
      const hostW = host.clientWidth;
      const hostH = host.clientHeight;
      if (!hostW || !hostH) return;
      const ui = stageEl.offsetWidth / width || 1;
      const s = fitStage(hostW, hostH, fit, width, height).s * zoom;
      const ox = (hostW - width * s) / 2 + panX;
      const oy = (fit === 'contain' ? (hostH - height * s) / 2 : Math.max(0, (hostH - height * s) / 2)) + panY;
      const next = { s, ui, ox, oy, hostW, hostH };
      setLayout((prev) => (sameLayout(prev, next) ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    ro.observe(stageEl);
    return () => ro.disconnect();
  }, [stageEl, width, height, fit, zoom, panX, panY]);

  // the shared/ui PortalContainerProvider takes a ref; keep it in sync with the (stable) state setter ref
  useLayoutEffect(() => {
    portalRef.current = portalEl;
  }, [portalEl]);

  const onLayoutRef = useRef(onLayout);
  useLayoutEffect(() => {
    onLayoutRef.current = onLayout;
  });
  useLayoutEffect(() => {
    if (layout) onLayoutRef.current?.({ scale: layout.s, uiScale: layout.ui, offset: { x: layout.ox, y: layout.oy }, host: { w: layout.hostW, h: layout.hostH } });
  }, [layout]);

  const ctx: StageContextValue = {
    width,
    height,
    fit,
    scale: layout?.s ?? 1,
    uiScale: layout?.ui ?? 1,
    ready: layout !== null,
    stageElement: stageEl,
    portalContainer: portalEl,
    clientToStage: (e) => (stageEl ? clientToStage(e, stageEl, width) : { x: 0, y: 0, s: 1 }),
    stageToClient: (p) => (stageEl ? stageToClient(p, stageEl, width) : p),
  };

  const k = layout ? layout.s / layout.ui : 1;

  return (
    <div
      ref={hostRef}
      data-stage-host=""
      className={cn(
        'relative size-full bg-primary-bg-1000',
        fit === 'width' ? 'overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable]' : 'overflow-hidden',
        className,
      )}
      {...rest}
    >
      {fit === 'width' && layout && <div aria-hidden style={{ width: 1, height: Math.max(0, layout.oy + height * layout.s) }} />}
      <div
        ref={setStageEl}
        data-stage=""
        className={cn('absolute top-0 left-0 origin-top-left', clip && 'overflow-hidden', stageClassName)}
        style={{
          width: dp(width),
          height: dp(height),
          transform: layout ? `translate(${layout.ox}px, ${layout.oy}px) scale(${k})` : undefined,
          visibility: layout ? undefined : 'hidden',
          ...stageStyle,
        }}
      >
        <StageContext value={ctx}>
          <PortalContainerProvider container={portalRef}>{children}</PortalContainerProvider>
          <div
            ref={setPortalEl}
            data-stage-portal=""
            className="pointer-events-none absolute inset-0 z-[1000] *:pointer-events-auto"
          />
        </StageContext>
      </div>
      {overlay}
    </div>
  );
}

/** Alias used by the specs (§8.2). */
export const ScaledViewport = Stage;

/** The enclosing stage. Throws outside a `<Stage>`. */
export function useStage(): StageContextValue {
  const ctx = use(StageContext);
  if (!ctx) throw new Error('useStage() must be used inside <Stage>');
  return ctx;
}

/** The enclosing stage, or null (components that also render outside a stage). */
export function useStageOptional(): StageContextValue | null {
  return use(StageContext);
}

/** Current visual scale (CSS px per design px); 1 outside a stage. */
export function useStageScale(): number {
  return use(StageContext)?.scale ?? 1;
}

/** Portal container inside the stage (undefined outside a stage → Base UI portals to `<body>`). */
export function useStagePortal(): HTMLElement | undefined {
  return use(StageContext)?.portalContainer ?? undefined;
}

/* ───────────────────────── editor helper ───────────────────────── */

export interface StagePoint extends Point {
  /** effective scale at the time of the event */
  s: number;
}

export interface StageDragOptions<T> {
  /** Called on pointerdown with the stage point. Return `false` to ignore the gesture. The return value is passed on. */
  onStart?: (start: StagePoint, e: ReactPointerEvent<Element>) => T | false;
  /** rAF-throttled; `delta` is in stage px */
  onMove: (p: StagePoint, delta: Point, data: T, e: PointerEvent) => void;
  onEnd?: (p: StagePoint, delta: Point, data: T, e: PointerEvent) => void;
  onCancel?: (data: T) => void;
  /** mouse buttons that start a drag (default: primary only) */
  buttons?: number[];
}

/**
 * Pointer-capture drag in stage coordinates (02-constructor §8.3):
 * `const onPointerDown = useStageDrag({ onStart: () => ({ x: el.x, y: el.y }), onMove: (_p, d, el0) => move(el0.x + d.x, el0.y + d.y) })`.
 */
export function useStageDrag<T = undefined>(options: StageDragOptions<T>): (e: ReactPointerEvent<Element>) => void {
  const stage = useStage();
  const optionsRef = useRef(options);
  useLayoutEffect(() => {
    optionsRef.current = options;
  });

  return (e) => {
    const buttons = optionsRef.current.buttons ?? [0];
    if (!buttons.includes(e.button)) return;
    const start = stage.clientToStage(e);
    const data = optionsRef.current.onStart ? optionsRef.current.onStart(start, e) : (undefined as T);
    if (data === false) return;
    const target = e.currentTarget;
    const pointerId = e.pointerId;
    target.setPointerCapture?.(pointerId);
    let frame = 0;
    let last: PointerEvent | null = null;
    const point = (ev: PointerEvent) => stage.clientToStage(ev);
    const flush = () => {
      frame = 0;
      if (!last) return;
      const p = point(last);
      optionsRef.current.onMove(p, { x: p.x - start.x, y: p.y - start.y }, data as T, last);
    };
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      last = ev;
      if (!frame) frame = requestAnimationFrame(flush);
    };
    const finish = (ev: PointerEvent, cancelled: boolean) => {
      if (ev.pointerId !== pointerId) return;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      target.removeEventListener('pointermove', move as EventListener);
      target.removeEventListener('pointerup', up as EventListener);
      target.removeEventListener('pointercancel', cancel as EventListener);
      if (target.hasPointerCapture?.(pointerId)) target.releasePointerCapture(pointerId);
      if (cancelled) return optionsRef.current.onCancel?.(data as T);
      const p = point(ev);
      optionsRef.current.onMove(p, { x: p.x - start.x, y: p.y - start.y }, data as T, ev);
      optionsRef.current.onEnd?.(p, { x: p.x - start.x, y: p.y - start.y }, data as T, ev);
    };
    const up = (ev: PointerEvent) => finish(ev, false);
    const cancel = (ev: PointerEvent) => finish(ev, true);
    target.addEventListener('pointermove', move as EventListener);
    target.addEventListener('pointerup', up as EventListener);
    target.addEventListener('pointercancel', cancel as EventListener);
  };
}
